import { Box, Button, Chip, Paper, Tooltip, Typography } from '@mui/material';
import React from 'react';

import {
  ReviewManualPromotion,
  ReviewRankOrdersSameRankOrder,
  ReviewRankOrdersWholeOrder,
} from '@/generated/types/typescript-axios';

type RankDisplayProps = {
  wholeOrder?: ReviewRankOrdersWholeOrder;
  sameRankOrder?: ReviewRankOrdersSameRankOrder;
  currentRank?: string;
  nextRank?: string;
  manualPromotion?: ReviewManualPromotion;
  onPromote: () => void;
  onCancelPromote: () => void;
  submitting?: boolean;
};

const RankDisplay: React.FC<RankDisplayProps> = ({
  wholeOrder,
  sameRankOrder,
  currentRank,
  nextRank,
  manualPromotion,
  onPromote,
  onCancelPromote,
  submitting,
}) => {
  const isPromoted = !!manualPromotion?.is_promoted;
  const canPromote = !!manualPromotion?.can_promote;
  const disabled = !canPromote || !!submitting;

  // ラベルに「割合外で」「割合の結果に関わらず」を入れない。
  // サーバが24日まで知り得ない「割合では上がらない」という事実を断言してしまうため。
  const buttonLabel = isPromoted ? '手動昇級を取り消す' : '手動昇級';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
        {/* 全体順位と同位順位の表示 */}
        {wholeOrder && sameRankOrder && (
          <>
            <Paper variant="outlined" sx={{ py: 1, px: 2 }}>
              <Typography variant="body2">全体順位</Typography>
              <Typography variant="h4">
                {wholeOrder.order === 0 ? '-' : wholeOrder.order}/
                {wholeOrder.total === 0 ? '-' : wholeOrder.total}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ py: 1, px: 2 }}>
              <Typography variant="body2">同位順位</Typography>
              <Typography variant="h4">
                {sameRankOrder.order === 0 ? '-' : sameRankOrder.order}/
                {sameRankOrder.total === 0 ? '-' : sameRankOrder.total}
              </Typography>
              {/* 分母は「その時点で採点済みの人数」で月内に増え続ける。
                  数字が動く理由を画面上で説明する唯一の場所なので削らないこと。 */}
              {sameRankOrder.submitted_total !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  採点済 {sameRankOrder.total ?? 0}/{sameRankOrder.submitted_total}件
                </Typography>
              )}
            </Paper>
          </>
        )}
        {/* 採点前後の段級位 */}
        <Paper variant="outlined" sx={{ py: 1, px: 2 }}>
          <Typography variant="body2">採点前</Typography>
          <Typography variant="h4">{currentRank || '未登録'}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ py: 1, px: 2 }}>
          <Typography variant="body2">採点後</Typography>
          {/* 括弧が無いと審査員は採点後の数字を確定した予測として読む。
              割合判定は24日の集計まで確定しない。 */}
          <Typography variant="h4">{nextRank ? `${nextRank}（暫定）` : '集計中'}</Typography>
        </Paper>
        {isPromoted && <Chip size="small" color="warning" label="手動" />}
      </Box>

      {manualPromotion && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0.5,
            mb: 2,
          }}
        >
          {/* disabled な子要素はイベントを拾えないので span で包む。
              tooltip だけではタッチ端末で読めないため caption も常時出す。 */}
          <Tooltip title={manualPromotion.reason ?? ''}>
            <span>
              <Button
                variant={isPromoted ? 'outlined' : 'contained'}
                color={isPromoted ? 'inherit' : 'primary'}
                disabled={disabled}
                onClick={isPromoted ? onCancelPromote : onPromote}
              >
                {buttonLabel}
              </Button>
            </span>
          </Tooltip>

          {!canPromote && manualPromotion.reason && (
            <Typography variant="caption" color="text.secondary">
              {manualPromotion.reason}
            </Typography>
          )}

          {isPromoted && manualPromotion.promoted_by_name && (
            // 誰の判断を消そうとしているのかを示す。取消はハード DELETE で痕跡が残らないため、
            // これが取消に対する唯一の抑止になる。
            <Typography variant="caption" color="text.secondary">
              {manualPromotion.promoted_by_name} が {manualPromotion.promoted_at} に記録
            </Typography>
          )}

          {!isPromoted && manualPromotion.ratio_would_promote && (
            // 押しても採点後の数字が動かない理由の説明であり、R2（2段は上がらない）を
            // 審査員の言葉で提示する場所でもある。ボタンは有効のままにする。
            <Typography variant="caption" color="text.secondary">
              この生徒は割合でも昇級見込みです（暫定）。記録しても上がるのは1段までです。
            </Typography>
          )}

          {!isPromoted && canPromote && !manualPromotion.ratio_would_promote && (
            <Typography variant="caption" color="text.secondary">
              24日時点の段級位から1段上がります
            </Typography>
          )}

          {manualPromotion.handbook_promoted_count !== undefined && (
            <Typography variant="caption" color="text.secondary">
              この手本で手動昇級済み: {manualPromotion.handbook_promoted_count}件
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RankDisplay;
